<template>
  <div>
    <ContentHeader />

    <!-- Loading -->
    <div v-if="loading" class="loading">
      <div class="loader"></div>
      <img
        src="@/assets/img/logo.jpg"
        alt="logo"
        width="496"
        height="99"
        class="loaderLogo"
      />
    </div>

    <!-- Banner -->
    <div class="sparePartBanner">
      <span>Spare Parts</span>
    </div>

    <section class="spareparts-section py-5 bg-light">
      <div class="container">

        <!-- Title -->
        <div class="text-center mb-4">
          <h1 class="leadTitle">Spare Parts</h1>
          <p class="text-muted">
            Genuine elevator and lift spare parts from
            <strong>Elemax-Tech Engineering Co., Ltd.</strong>
          </p>
        </div>

        <!-- Tabs -->
        <div class="tabs-container text-center mb-4">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'electrical' }"
            @click="activeTab = 'electrical'"
          >
            Electrical
          </button>

          <button
            class="tab-btn"
            :class="{ active: activeTab === 'mechanical' }"
            @click="activeTab = 'mechanical'"
          >
            Mechanical
          </button>
        </div>

        <!-- Search -->
        <div class="row justify-content-center mb-4">
          <div class="col-md-6">
            <input
              v-model="searchQuery"
              type="text"
              class="form-control shadow-sm"
              placeholder="Search spare parts..."
            />
          </div>
        </div>

        <!-- Spare Parts Grid -->
        <div class="row g-4">
          <div
            class="col-md-3 col-sm-6"
            v-for="part in paginatedActiveTab"
            :key="part.id"
          >
            <div class="card border-0 shadow-sm h-100">
              <img
                :src="fullImageUrl(part.image)"
                :alt="part.name"
                class="card-img-top"
              />
              <div class="card-body">
                <h5 class="card-title fw-bold">{{ part.name }}</h5>
                <p class="card-text text-muted">{{ part.description }}</p>
                <p class="fw-semibold text-primary mb-0">$ {{ part.price }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="activeTotalPages > 1" class="text-center mt-4">
          <button
            class="btn btn-outline-primary me-2"
            :disabled="activePage === 1"
            @click="prevPage"
          >
            &lt;
          </button>

          <span>{{ activePage }} / {{ activeTotalPages }}</span>

          <button
            class="btn btn-outline-primary ms-2"
            :disabled="activePage === activeTotalPages"
            @click="nextPage"
          >
            &gt;
          </button>
        </div>

        <!-- Contact -->
        <div v-if="contacts" class="text-center mt-5">
          <h4 class="fw-bold mb-3">Need a Replacement Part?</h4>
          <p class="text-muted">
            Contact our team for pricing, availability, or technical assistance.
          </p>

          <p class="fw-bold text-dark mb-2">
            📞 Call us:
            <span
              v-for="(phone, index) in normalizedSparePhones"
              :key="'spare-'+index"
            >
              <a
                :href="'tel:' + normalizedSparePhones[index]"
                class="text-primary text-decoration-none me-2"
              >
                {{ normalizedSparePhones[index] }}
              </a>
            </span>
          </p>
        </div>

      </div>
    </section>

    <ContentFooter />
  </div>
</template>

<script>
import ContentHeader from "@/components/ContentHeader.vue";
import ContentFooter from "@/components/ContentFooter.vue";
import axios from "axios";

export default {
  name: "SpareParts",
  components: { ContentHeader, ContentFooter },

  data() {
    return {
      loading: true,

      // ui
      searchQuery: "",
      activeTab: "electrical",

      // api
      apiBase: process.env.VUE_APP_API_BASE,
      apiToken: process.env.VUE_APP_API_TOKEN,

      spare_parts: [],

      // pagination
      electricalPage: 1,
      mechanicalPage: 1,
      itemsPerPage: 8,

      // contacts section
      contacts: true,
      spare_phones: ["0969630514", "0945001122"], // example phones
    };
  },
  
  computed: {
    // normalizedSparePhones() {
    //   return this.spare_phones.map((p) => p.toString().trim());
    // },

	normalizedSparePhones() {
      if (!this.contacts || !this.contacts.spareparts_phone) return [];
      if (Array.isArray(this.contacts.spareparts_phone)) return this.contacts.spareparts_phone;
      try {
        const parsed = JSON.parse(this.contacts.spareparts_phone);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        return [this.contacts.spareparts_phone];
      }
    },

    electricalParts() {
      return this.filterParts("electrical");
    },
    mechanicalParts() {
      return this.filterParts("mechanical");
    },

    activePage() {
      return this.activeTab === "electrical"
        ? this.electricalPage
        : this.mechanicalPage;
    },

    activeTotalPages() {
      const list =
        this.activeTab === "electrical"
          ? this.electricalParts
          : this.mechanicalParts;

      return Math.ceil(list.length / this.itemsPerPage) || 1;
    },

    paginatedActiveTab() {
      const list =
        this.activeTab === "electrical"
          ? this.electricalParts
          : this.mechanicalParts;

      const page = this.activePage;

      const start = (page - 1) * this.itemsPerPage;
      return list.slice(start, start + this.itemsPerPage);
    },
  },

  methods: {
    filterParts(category) {
      const query = this.searchQuery.toLowerCase();

      return this.spare_parts
        .filter((p) => p.category === category)
        .filter(
          (p) =>
            p.name.toLowerCase().includes(query) ||
            p.description.toLowerCase().includes(query)
        );
    },

    prevPage() {
      if (this.activePage > 1) {
        if (this.activeTab === "electrical") this.electricalPage--;
        else this.mechanicalPage--;
      }
    },

    nextPage() {
      if (this.activePage < this.activeTotalPages) {
        if (this.activeTab === "electrical") this.electricalPage++;
        else this.mechanicalPage++;
      }
    },

    async fetchData() {
      try {
        const res = await axios.get(`${this.apiBase}/api/spare-parts`, {
          headers: { Authorization: `Bearer ${this.apiToken}` },
        });

        this.spare_parts = res.data;
      } catch (err) {
        console.error("API Error:", err);
      } finally {
        this.loading = false;
      }
    },

    fullImageUrl(img) {
      if (!img) return require("@/assets/img/spare_parts.jpg");

      // if already full URL
      if (img.startsWith("http")) return img;

      // ensure proper joining
      return `${this.apiBase.replace(/\/$/, "")}/${img.replace(/^\//, "")}`;
    },
  },

  watch: {
    searchQuery() {
      this.electricalPage = 1;
      this.mechanicalPage = 1;
    },
    activeTab() {
      this.electricalPage = 1;
      this.mechanicalPage = 1;
    },
  },

  async mounted() {
	try {
	const baseURL = this.apiBase;
      const token = this.apiToken; // your token
      const response = await axios.get(`${baseURL}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      this.contacts = response.data;
    } catch (error) {
      console.error(error);
    }
    this.fetchData();
  },
};
</script>

<style scoped>
.sparePartBanner {
  background-color: #0d6efd;
  color: #fff;
  text-align: center;
  padding: 40px 0;
  font-size: 1.5rem;
}

.tabs-container {
  display: flex;
  justify-content: center;
  gap: 15px;
}

.tab-btn {
  padding: 8px 20px;
  border: none;
  background: #eee;
  border-radius: 30px;
  font-weight: 600;
  cursor: pointer;
}

.tab-btn.active {
  background: #0d6efd;
  color: #fff;
}

/* .card-img-top {
  height: 180px;
  object-fit: cover;
} */
</style>
